import React, { FC, RefObject, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import clsx from 'clsx';
import InputText from 'components/common/InputText';
import { isEmptyString } from 'utils/helper';
import Button from 'components/common/button';
import ButtonGroup from '@mui/material/ButtonGroup';
import isNil from 'lodash/isNil';

// Type definition matching SignaturePad.Point
interface SignaturePoint {
  x: number;
  y: number;
  time?: number;
  pressure?: number;
}

// Type definition for drawing strokes
type DrawingData = SignaturePoint[][];

type SignatureBoxProps = {
  mode: 'draw' | 'type';
  onModeChange: (mode: 'draw' | 'type') => void;
  canvasRef: RefObject<SignatureCanvas>;
  boxRef: RefObject<HTMLDivElement>;
  canvasWidth: number;
  canvasHeight: number;
  typedValue: string;
  onDraw: (data: string) => void;
  onType: (value: string) => void;
  onClear: () => void;
  setInteracted: (value: boolean) => void;
  isDisabled: boolean;
  placeholder: string;
  inputId: string;
  drawingData?: DrawingData;
  onSaveDrawingData?: (data: DrawingData) => void;
};

type SignatureBoxFooterProps = {
  onClear: () => void;
  disabled: boolean;
};

function SignatureBoxFooter({ onClear, disabled }: SignatureBoxFooterProps) {
  return (
    <>
      <div className="border-b-2 border-gray-300 mt-4" />
      <div className="flex justify-between items-center mt-3">
        <span className="text-sm text-gray-500 truncate">
          Sign above
        </span>
        <button
          type="button"
          className="text-sm text-gray-500 underline flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onClear}
          disabled={disabled}
        >
          Clear
        </button>
      </div>
    </>
  );
}

const SignatureBox: FC<SignatureBoxProps> = function SignatureBox({
  mode,
  onModeChange,
  canvasRef,
  boxRef,
  canvasWidth,
  canvasHeight,
  typedValue,
  onDraw,
  onType,
  onClear,
  setInteracted,
  isDisabled,
  placeholder,
  inputId,
  drawingData,
  onSaveDrawingData,
}) {
  const isDrawing = mode === 'draw';
  const isTyping = mode === 'type';

  // Restore drawing when switching back to draw mode
  useEffect(() => {
    if (isDrawing && drawingData && canvasRef?.current) {
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!isNil(canvas)) {
          // Use type casting for compatibility - the data structure is compatible
          // even though TypeScript doesn't recognize all Point properties
          (canvas.fromData as (data: DrawingData) => void)(drawingData);
        }
      }, 50);
    }
  }, [isDrawing, drawingData, canvasRef]);

  return (
    <div className="flex flex-col gap-4">
      <ButtonGroup
        variant="outlined"
        color="inherit"
        aria-label="Signature mode"
      >
        <Button
          variant="outlined"
          disabled={isDisabled}
          classWrap="!w-fit"
          className={clsx(
            'flex gap-2 !h-8 !min-w-0 !px-3 !normal-case !border-gray-300',
            {
              '!bg-populate-green !text-white': isTyping,
              'text-gray-600 bg-transparent': !isTyping,
            },
          )}
          onClick={() => { onModeChange('type'); }}
        >
          Type
        </Button>
        <Button
          variant="outlined"
          disabled={isDisabled}
          classWrap="!w-fit"
          className={clsx(
            'flex gap-2 !h-8 !min-w-0 !px-3 !normal-case !border-gray-300',
            {
              '!bg-populate-green !text-white': isDrawing,
              'text-gray-600 bg-transparent': !isDrawing,
            },
          )}
          onClick={() => { onModeChange('draw'); }}
        >
          Draw
        </Button>
      </ButtonGroup>

      {isDrawing && (
        <div className="max-w-full">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div ref={boxRef} className="w-full max-w-[420px]">
              <SignatureCanvas
                ref={canvasRef}
                penColor="#345D7E"
                canvasProps={{
                  width: canvasWidth,
                  height: canvasHeight,
                  className: clsx(
                    'w-full border-0 signature-canvas-pen-cursor block touch-none',
                    {
                      'opacity-50 pointer-events-none': isDisabled,
                    },
                  ),
                }}
                onEnd={() => {
                  setInteracted(true);
                  const data = (canvasRef as any)?.current?.toDataURL?.('image/png') ?? '';
                  onDraw(data);
                  // Save drawing strokes for restoration
                  const drawingStrokes = (canvasRef as any)?.current?.toData?.();
                  onSaveDrawingData?.(drawingStrokes);
                }}
              />
            </div>
            <SignatureBoxFooter onClear={onClear} disabled={isDisabled} />
          </div>
        </div>
      )}

      {isTyping && (
        <div className="max-w-full">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <InputText
              value={typedValue}
              id={inputId}
              type="text"
              placeholder={placeholder}
              className="!mb-0 w-full"
              disabled={isDisabled}
              onChange={(e) => {
                setInteracted(true);
                onType(e?.target?.value ?? '');
              }}
            />
            <div className="bg-gray-50 rounded mt-4 p-6 min-h-24 flex items-center justify-center">
              {!isEmptyString(typedValue) && (
                <p
                  className="text-4xl dancing-script-regular signature-color"
                >
                  {typedValue}
                </p>
              )}
            </div>
            <SignatureBoxFooter onClear={onClear} disabled={isDisabled} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureBox;
